import Dexie, { type Table } from "dexie";

import type {
  EligibilityStatus,
  FitGrade,
  NormalizedNotice,
  NoticeChange,
  NoticeEvaluation,
  SyncRun,
  UserNotice,
} from "../notices/types";
import {
  DATABASE_SCHEMA_VERSION,
  registerDatabaseMigrations,
} from "./migrations";

export const DATABASE_NAME = "ai-support-monitor";

export interface RemoteNoticeRecord extends NormalizedNotice {
  /** Client-only query field; it is not part of the 24-field Git contract. */
  region: string;
  /** Client-only tombstone; it is never written back to the Git snapshot. */
  sourceMissing: boolean;
  eligibilityStatus: EligibilityStatus | null;
  fitScore: number | null;
  fitGrade: FitGrade | null;
}

export type RemoteChangeRecord = NoticeChange;
export type RemoteEvaluationRecord = NoticeEvaluation;

export interface SettingRecord<T = unknown> {
  key: string;
  value: T;
  updatedAt: string;
}

export type KnownSettingKey =
  | "databaseSchemaVersion"
  | "lastSyncDatasetVersion"
  | "lastSyncContentHash"
  | "lastSyncAt"
  | "defaultFilters"
  | "defaultSort"
  | "storagePersistence"
  | "lastBackupAt"
  | "reportPreferences"
  | "companyProfile";

export type BrowserDatabaseOptions = NonNullable<
  ConstructorParameters<typeof Dexie>[1]
>;

export class BrowserDatabase extends Dexie {
  remoteNotices!: Table<RemoteNoticeRecord, string>;
  remoteChanges!: Table<RemoteChangeRecord, string>;
  remoteEvaluations!: Table<RemoteEvaluationRecord, string>;
  userNotices!: Table<UserNotice, string>;
  syncRuns!: Table<SyncRun, string>;
  settings!: Table<SettingRecord, string>;

  constructor(
    name = DATABASE_NAME,
    options?: BrowserDatabaseOptions,
  ) {
    super(name, options);
    registerDatabaseMigrations(this);
  }
}

export class BrowserDatabaseUnavailableError extends Error {
  constructor() {
    super("IndexedDB is unavailable in this environment.");
    this.name = "BrowserDatabaseUnavailableError";
  }
}

export function isBrowserDatabaseAvailable(): boolean {
  return typeof globalThis.indexedDB !== "undefined";
}

export function createBrowserDatabase(
  options?: BrowserDatabaseOptions,
  name = DATABASE_NAME,
): BrowserDatabase {
  if (!options?.indexedDB && !isBrowserDatabaseAvailable()) {
    throw new BrowserDatabaseUnavailableError();
  }

  return new BrowserDatabase(name, options);
}

let defaultDatabase: BrowserDatabase | null = null;

/** Lazily opens IndexedDB so importing this module remains SSR-safe. */
export async function openBrowserDatabase(): Promise<BrowserDatabase> {
  if (!defaultDatabase) {
    defaultDatabase = createBrowserDatabase();
  }
  if (!defaultDatabase.isOpen()) {
    await defaultDatabase.open();
  }
  return defaultDatabase;
}

/** Primarily useful for sign-out, tests, and hot-module replacement. */
export function closeBrowserDatabase(): void {
  defaultDatabase?.close();
  defaultDatabase = null;
}

export { DATABASE_SCHEMA_VERSION };
